import { useRef, useState, useCallback, useEffect } from 'react';
import type { EditorElement, EditorElementStyle, AspectRatio, AdAnimation } from '../types';

interface Props {
  imageUrl: string;
  aspectRatio: AspectRatio;
  elements: EditorElement[];
  selectedId: string | null;
  overlayOpacity: number;
  animation: AdAnimation;
  imageFit?: 'cover' | 'contain';
  imagePositionX?: number;
  imagePositionY?: number;
  onSelectElement: (id: string | null) => void;
  onUpdatePosition: (id: string, x: number, y: number) => void;
  onUpdateElement: (id: string, updates: Partial<EditorElement>) => void;
  /** Called once when a drag or resize interaction begins — push history snapshot here */
  onInteractionStart?: () => void;
  onImagePositionChange?: (x: number, y: number) => void;
  containerRef?: React.RefObject<HTMLDivElement>;
  isExporting?: boolean;
}

const RATIO_CLASS: Record<AspectRatio, string> = {
  '1:1':  'aspect-square max-w-[540px]',
  '9:16': 'aspect-[9/16] max-w-[338px]',
  '16:9': 'aspect-video max-w-[860px]',
};

const HANDLE_CURSORS: Record<string, string> = {
  nw: 'nw-resize',
  ne: 'ne-resize',
  sw: 'sw-resize',
  se: 'se-resize',
};

function getElementStyle(el: EditorElement, editing: boolean): React.CSSProperties {
  const s = el.style;
  return {
    position: 'absolute',
    left: `${el.x}%`,
    top: `${el.y}%`,
    fontFamily: s.fontFamily,
    fontSize: `${s.fontSize}px`,
    fontWeight: s.fontWeight,
    color: s.color,
    backgroundColor: s.backgroundColor === 'transparent' ? 'transparent' : s.backgroundColor,
    borderRadius: `${s.borderRadius}px`,
    padding: `${s.paddingY}px ${s.paddingX}px`,
    opacity: s.opacity,
    letterSpacing: `${s.letterSpacing}px`,
    lineHeight: s.lineHeight,
    textAlign: s.textAlign,
    textTransform: s.textTransform,
    border: s.border === 'none' ? 'none' : s.border,
    textShadow: s.textShadow ? '0 2px 8px rgba(0,0,0,0.7)' : 'none',
    cursor: el.locked ? 'default' : 'grab',
    userSelect: editing ? 'text' : 'none',
    whiteSpace: 'pre-wrap',
    maxWidth: s.width ? undefined : undefined, // width overrides below
    width: s.width ? `${s.width}%` : (el.type === 'button' || el.type === 'shape' ? 'auto' : 'auto'),
    height: s.height ? `${s.height}%` : undefined,
    minHeight: s.height ? undefined : undefined,
    overflow: s.height ? 'hidden' : 'visible',
    display: el.visible ? 'block' : 'none',
    boxSizing: 'border-box',
    zIndex: 10,
  };
}

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se';

interface ResizeState {
  id: string;
  handle: ResizeHandle;
  startMouseX: number;
  startMouseY: number;
  startElX: number;
  startElY: number;
  startWidth: number;
  startHeight: number;
  startStyle: EditorElementStyle;
}

export default function EditorCanvas({
  imageUrl,
  aspectRatio,
  elements,
  selectedId,
  overlayOpacity,
  animation,
  imageFit = 'cover',
  imagePositionX = 50,
  imagePositionY = 50,
  onSelectElement,
  onUpdatePosition,
  onUpdateElement,
  onInteractionStart,
  onImagePositionChange,
  containerRef,
  isExporting = false,
}: Props) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const elementRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const setRefs = (node: HTMLDivElement | null) => {
    (canvasRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    if (containerRef) {
      (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    }
  };

  const [editingId, setEditingId] = useState<string | null>(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDims, setResizeDims] = useState<{ w: number; h: number } | null>(null);

  const dragRef = useRef<{
    id: string;
    startX: number;
    startY: number;
    elStartX: number;
    elStartY: number;
  } | null>(null);

  const panRef = useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
    hasMoved: boolean;
  } | null>(null);

  const resizeRef = useRef<ResizeState | null>(null);

  const canPan = imageFit === 'cover' && !!onImagePositionChange;

  // ── Resize start ────────────────────────────────────────────────
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, el: EditorElement, handle: ResizeHandle) => {
      e.stopPropagation();
      e.preventDefault();

      const canvas = canvasRef.current;
      const node = elementRefs.current.get(el.id);
      if (!canvas || !node) return;

      const canvasRect = canvas.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();

      // Use stored width/height if available, otherwise measure from DOM
      const startWidth = el.style.width ?? ((nodeRect.width / canvasRect.width) * 100);
      const startHeight = el.style.height ?? ((nodeRect.height / canvasRect.height) * 100);

      onInteractionStart?.();
      onSelectElement(el.id);
      resizeRef.current = {
        id: el.id,
        handle,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startElX: el.x,
        startElY: el.y,
        startWidth,
        startHeight,
        startStyle: { ...el.style },
      };
      setResizeDims({ w: startWidth, h: startHeight });
      setIsResizing(true);
    },
    [onSelectElement, onInteractionStart],
  );

  // ── Element drag start ──────────────────────────────────────────
  const handleElementMouseDown = useCallback(
    (e: React.MouseEvent, el: EditorElement) => {
      if (el.locked || editingId === el.id) return;
      e.stopPropagation();
      onInteractionStart?.();
      onSelectElement(el.id);
      dragRef.current = {
        id: el.id,
        startX: e.clientX,
        startY: e.clientY,
        elStartX: el.x,
        elStartY: el.y,
      };
    },
    [editingId, onSelectElement, onInteractionStart],
  );

  // ── Canvas pan start ────────────────────────────────────────────
  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      const isBg = target === canvasRef.current || !!target.dataset.canvasBg;
      if (!isBg || !canPan) return;
      panRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPosX: imagePositionX,
        startPosY: imagePositionY,
        hasMoved: false,
      };
      setIsPanning(true);
    },
    [canPan, imagePositionX, imagePositionY],
  );

  // ── Global mousemove / mouseup ──────────────────────────────────
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();

      // ── Resize ──────────────────────────────────────────────────
      if (resizeRef.current) {
        const { id, handle, startMouseX, startMouseY,
                startElX, startElY, startWidth, startHeight, startStyle } = resizeRef.current;

        const dx = ((e.clientX - startMouseX) / rect.width) * 100;
        const dy = ((e.clientY - startMouseY) / rect.height) * 100;

        let newX = startElX;
        let newY = startElY;
        let newWidth = startWidth;
        let newHeight = startHeight;

        switch (handle) {
          case 'se':
            newWidth  = Math.max(5, startWidth + dx);
            newHeight = Math.max(3, startHeight + dy);
            break;
          case 'sw':
            newWidth  = Math.max(5, startWidth - dx);
            newX      = Math.max(0, startElX + dx);
            newHeight = Math.max(3, startHeight + dy);
            break;
          case 'ne':
            newWidth  = Math.max(5, startWidth + dx);
            newHeight = Math.max(3, startHeight - dy);
            newY      = Math.max(0, startElY + dy);
            break;
          case 'nw':
            newWidth  = Math.max(5, startWidth - dx);
            newX      = Math.max(0, startElX + dx);
            newHeight = Math.max(3, startHeight - dy);
            newY      = Math.max(0, startElY + dy);
            break;
        }

        // Clamp to canvas bounds
        newWidth  = Math.min(100 - newX, newWidth);
        newHeight = Math.min(100 - newY, newHeight);

        onUpdateElement(id, {
          x: newX,
          y: newY,
          style: { ...startStyle, width: newWidth, height: newHeight },
        });
        setResizeDims({ w: newWidth, h: newHeight });
        return;
      }

      // ── Element drag ─────────────────────────────────────────────
      if (dragRef.current) {
        const dx = ((e.clientX - dragRef.current.startX) / rect.width) * 100;
        const dy = ((e.clientY - dragRef.current.startY) / rect.height) * 100;
        const newX = Math.max(0, Math.min(90, dragRef.current.elStartX + dx));
        const newY = Math.max(0, Math.min(90, dragRef.current.elStartY + dy));
        onUpdatePosition(dragRef.current.id, newX, newY);
        return;
      }

      // ── Image pan ────────────────────────────────────────────────
      if (panRef.current && onImagePositionChange) {
        const dx = e.clientX - panRef.current.startX;
        const dy = e.clientY - panRef.current.startY;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) panRef.current.hasMoved = true;
        const sensitivity = 1.2;
        const newX = Math.max(0, Math.min(100,
          panRef.current.startPosX - (dx / rect.width) * 100 * sensitivity,
        ));
        const newY = Math.max(0, Math.min(100,
          panRef.current.startPosY - (dy / rect.height) * 100 * sensitivity,
        ));
        onImagePositionChange(newX, newY);
      }
    };

    const handleMouseUp = () => {
      resizeRef.current = null;
      dragRef.current = null;
      panRef.current = null;
      setIsResizing(false);
      setIsPanning(false);
      setResizeDims(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [onUpdatePosition, onUpdateElement, onImagePositionChange]);

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent, el: EditorElement) => {
      if (el.locked || el.type === 'shape' || el.type === 'image') return;
      e.stopPropagation();
      setEditingId(el.id);
    },
    [],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>, id: string) => {
      const newContent = e.currentTarget.innerText;
      onUpdateElement(id, { content: newContent });
      setEditingId(null);
    },
    [onUpdateElement],
  );

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent) => {
      if (panRef.current?.hasMoved) return;
      const target = e.target as HTMLElement;
      const isBg = target === canvasRef.current || !!target.dataset.canvasBg;
      if (isBg) {
        onSelectElement(null);
        setEditingId(null);
      }
    },
    [onSelectElement],
  );

  const animImageStyle: React.CSSProperties =
    animation === 'ken-burns' && imgLoaded
      ? { animation: 'editor-ken-burns 9s ease-in-out infinite', transformOrigin: 'center' }
      : {};

  const canvasCursor = isPanning ? 'grabbing' : isResizing ? 'auto' : canPan ? 'grab' : 'default';

  return (
    <>
      <style>{`
        @keyframes editor-ken-burns {
          0%   { transform: scale(1) translate(0,0); }
          33%  { transform: scale(1.09) translate(-2%,-1%); }
          66%  { transform: scale(1.06) translate(1.5%,-0.5%); }
          100% { transform: scale(1) translate(0,0); }
        }
        @keyframes editor-cta-ring {
          0%   { box-shadow: 0 0 0 0 rgba(249,115,22,.75); }
          70%  { box-shadow: 0 0 0 14px rgba(249,115,22,0); }
          100% { box-shadow: 0 0 0 0 rgba(249,115,22,0); }
        }
        @keyframes editor-spotlight {
          0%   { transform: translateX(-160%) skewX(-20deg); opacity:0; }
          8%   { opacity:0.55; }
          92%  { opacity:0.55; }
          100% { transform: translateX(260%) skewX(-20deg); opacity:0; }
        }
        @keyframes editor-text-reveal {
          0%   { opacity:0; transform:translateY(14px); }
          18%  { opacity:1; transform:translateY(0); }
          78%  { opacity:1; transform:translateY(0); }
          100% { opacity:0; transform:translateY(-10px); }
        }
        .editor-element-content:empty:before {
          content: attr(data-placeholder);
          color: rgba(255,255,255,0.4);
          pointer-events: none;
        }
        .resize-handle {
          position: absolute;
          width: 10px;
          height: 10px;
          background: #f97316;
          border: 2px solid white;
          border-radius: 2px;
          z-index: 30;
          transition: transform 0.1s;
        }
        .resize-handle:hover {
          transform: scale(1.3);
        }
      `}</style>

      <div
        ref={setRefs}
        className={`relative overflow-hidden rounded-xl select-none w-full ${RATIO_CLASS[aspectRatio]}`}
        style={{ minWidth: '260px', cursor: isExporting ? 'default' : canvasCursor }}
        onMouseDown={isExporting ? undefined : handleCanvasMouseDown}
        onClick={isExporting ? undefined : handleCanvasClick}
        data-canvas-bg="true"
      >
        {/* Background image */}
        <img
          src={imageUrl}
          alt="Ad background"
          className="absolute inset-0 w-full h-full"
          style={{
            ...animImageStyle,
            objectFit: imageFit,
            objectPosition: `${imagePositionX}% ${imagePositionY}%`,
            transition: isPanning ? 'none' : undefined,
          }}
          onLoad={() => setImgLoaded(true)}
          draggable={false}
          data-canvas-bg="true"
        />

        {/* Dark overlay */}
        <div
          className="absolute inset-0"
          style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }}
          data-canvas-bg="true"
        />

        {/* Spotlight animation */}
        {animation === 'spotlight' && imgLoaded && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '40%',
                height: '100%',
                background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,.35) 50%, transparent 70%)',
                animation: 'editor-spotlight 3.2s ease-in-out infinite',
              }}
            />
          </div>
        )}

        {/* Pan hint badge */}
        {canPan && !isPanning && !isExporting && !selectedId && (
          <div
            className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-black/50 text-white/60 text-xs px-2.5 py-1 rounded-full pointer-events-none"
            style={{ backdropFilter: 'blur(4px)', zIndex: 5 }}
          >
            <i className="ri-drag-move-line" />
            drag to reposition
          </div>
        )}

        {/* Elements */}
        {elements.map((el) => {
          const isSelected = selectedId === el.id && !isExporting;
          const isEditing  = editingId === el.id && !isExporting;
          const showHandles = isSelected && !isEditing && !el.locked;

          const ctaPulseStyle: React.CSSProperties =
            animation === 'cta-pulse' && el.type === 'button' && imgLoaded
              ? { animation: 'editor-cta-ring 1.6s ease-out infinite' }
              : {};

          const textRevealStyle: React.CSSProperties =
            animation === 'text-reveal' && el.type === 'text' && imgLoaded
              ? { animation: 'editor-text-reveal 4s ease-in-out infinite' }
              : {};

          return (
            <div
              key={el.id}
              ref={(node) => {
                if (node) elementRefs.current.set(el.id, node);
                else elementRefs.current.delete(el.id);
              }}
              style={{
                ...getElementStyle(el, isEditing),
                ...ctaPulseStyle,
                ...textRevealStyle,
                outline: isSelected && !isEditing
                  ? '2px solid #f97316'
                  : isEditing
                  ? '2px dashed rgba(249,115,22,.7)'
                  : 'none',
                outlineOffset: '2px',
                // Allow handles to visually overflow the element bounds slightly
                overflow: el.style.height ? 'hidden' : 'visible',
              }}
              onMouseDown={isExporting ? undefined : (e) => handleElementMouseDown(e, el)}
              onDoubleClick={isExporting ? undefined : (e) => handleDoubleClick(e, el)}
            >
              {/* Content */}
              {el.type === 'image' ? (
                <img
                  src={el.imageUrl ?? ''}
                  alt="Logo"
                  style={{
                    width: '100%',
                    height: el.style.height ? '100%' : 'auto',
                    objectFit: 'contain',
                    borderRadius: `${el.style.borderRadius}px`,
                    border: el.style.border === 'none' ? 'none' : el.style.border,
                    display: 'block',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    maxWidth: '100%',
                  }}
                  draggable={false}
                />
              ) : isEditing && el.type !== 'shape' ? (
                <div
                  contentEditable
                  suppressContentEditableWarning
                  className="editor-element-content outline-none min-w-[40px] min-h-[20px]"
                  data-placeholder="Type here..."
                  style={{
                    fontFamily: el.style.fontFamily,
                    fontSize: `${el.style.fontSize}px`,
                    fontWeight: el.style.fontWeight,
                    color: el.style.color,
                    letterSpacing: `${el.style.letterSpacing}px`,
                    lineHeight: el.style.lineHeight,
                    textAlign: el.style.textAlign,
                    textTransform: el.style.textTransform,
                    cursor: 'text',
                    userSelect: 'text',
                    whiteSpace: 'pre-wrap',
                  }}
                  onBlur={(e) => handleBlur(e, el.id)}
                  dangerouslySetInnerHTML={{ __html: el.content }}
                  ref={(node) => {
                    if (node) {
                      node.focus();
                      const range = document.createRange();
                      range.selectNodeContents(node);
                      const sel = window.getSelection();
                      sel?.removeAllRanges();
                      sel?.addRange(range);
                    }
                  }}
                />
              ) : (
                <span style={{ pointerEvents: 'none' }}>{el.content}</span>
              )}

              {/* Resize dimension badge */}
              {isSelected && isResizing && resizeDims && (
                <div
                  className="absolute flex items-center gap-1 bg-stone-900/90 text-white rounded-md pointer-events-none"
                  style={{
                    fontSize: '11px',
                    fontFamily: 'ui-monospace, monospace',
                    lineHeight: 1,
                    bottom: -28,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                    zIndex: 40,
                    padding: '4px 8px',
                    border: '1px solid rgba(249,115,22,0.5)',
                  }}
                >
                  <span style={{ color: '#f97316' }}>W</span>
                  <span>{resizeDims.w.toFixed(1)}%</span>
                  <span style={{ color: 'rgba(255,255,255,0.3)', margin: '0 2px' }}>×</span>
                  <span style={{ color: '#f97316' }}>H</span>
                  <span>{resizeDims.h.toFixed(1)}%</span>
                </div>
              )}

              {/* Move hint tooltip */}
              {isSelected && !isEditing && !el.locked && !isResizing && (
                <div
                  className="absolute flex items-center gap-1 bg-orange-500 text-white px-1.5 py-0.5 rounded-t-md pointer-events-none"
                  style={{ fontSize: '10px', lineHeight: 1, top: -20, left: 0, whiteSpace: 'nowrap', zIndex: 20 }}
                >
                  <i className="ri-drag-move-line" style={{ fontSize: '10px' }} />
                  {el.type === 'image' ? 'drag to move' : 'drag · dbl-click to edit'}
                </div>
              )}

              {/* Resize handles — 4 corners */}
              {showHandles && (
                <>
                  {/* NW */}
                  <div
                    className="resize-handle"
                    style={{ top: -1, left: -1, cursor: HANDLE_CURSORS.nw, transform: 'translate(-50%, -50%)' }}
                    onMouseDown={(e) => handleResizeStart(e, el, 'nw')}
                  />
                  {/* NE */}
                  <div
                    className="resize-handle"
                    style={{ top: -1, right: -1, cursor: HANDLE_CURSORS.ne, transform: 'translate(50%, -50%)' }}
                    onMouseDown={(e) => handleResizeStart(e, el, 'ne')}
                  />
                  {/* SW */}
                  <div
                    className="resize-handle"
                    style={{ bottom: -1, left: -1, cursor: HANDLE_CURSORS.sw, transform: 'translate(-50%, 50%)' }}
                    onMouseDown={(e) => handleResizeStart(e, el, 'sw')}
                  />
                  {/* SE */}
                  <div
                    className="resize-handle"
                    style={{ bottom: -1, right: -1, cursor: HANDLE_CURSORS.se, transform: 'translate(50%, 50%)' }}
                    onMouseDown={(e) => handleResizeStart(e, el, 'se')}
                  />
                </>
              )}
            </div>
          );
        })}

        {/* Empty canvas hint */}
        {!isExporting && elements.filter((e) => e.visible).length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center bg-stone-900/60 rounded-xl px-6 py-5">
              <i className="ri-add-circle-line text-stone-400 text-3xl block mb-2" />
              <p className="text-sm text-stone-400">Add elements using the toolbar above</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
